import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { Header } from "build-ts/metadata";
import JobList from "modules/analyst/containers/JobList";
import JobDetailView from "modules/analyst/components/JobDetailView";
import { AppIcons } from "constants/icons";

type WorkflowFilter = "all" | "live" | "paused";

const filterLabel: Record<WorkflowFilter, string> = {
  all: "All",
  live: "Live",
  paused: "Paused",
};

export interface AnalystJobsProps {
  analystId: string | undefined;
  jobs: Header[];
  isLoading: boolean;
  onRefresh: () => void;
  setIsViewingOrEditingJob?: (value: boolean) => void;
  initialJobId?: string;
  analystName?: string;
}

const AnalystJobs: React.FC<AnalystJobsProps> = ({
  analystId,
  jobs,
  isLoading,
  onRefresh,
  setIsViewingOrEditingJob,
  initialJobId,
  analystName,
}) => {
  const navigate = useNavigate();

  const [isCreatingOrViewingJob, setIsCreatingOrViewingJob] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Header | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMode, setFilterMode] = useState<WorkflowFilter>("all");

  const liveCount = jobs.filter(j => j.has_active_schedule === true).length;

  const filteredJobs = useMemo(() => {
    let result = jobs;
    if (filterMode === "live") result = result.filter(j => j.has_active_schedule === true);
    else if (filterMode === "paused") result = result.filter(j => j.has_active_schedule === false);
    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(j => j.name?.toLowerCase().includes(lower));
    }
    return result;
  }, [jobs, searchTerm, filterMode]);

  // Auto-open a specific job when arriving via linked route (/jobs/:jobId)
  useEffect(() => {
    if (!initialJobId || jobs.length === 0 || isCreatingOrViewingJob) return;
    const job = jobs.find(j => j.id === initialJobId);
    if (job) handleJobClick(job);
  }, [jobs, initialJobId]);

  const handleCreateNewJob = () => {
    if (analystId) {
      navigate("/chat/new", {
        state: {
          initialPrompt:
            "I want to create a new workflow. Guide me through the process and ask me the details" +
            " required to setup a workflow. Save when I am done.",
          analystId,
        },
      });
    }
  };

  const handleJobClick = (job: Header) => {
    setSelectedJob(job);
    setIsCreatingOrViewingJob(true);
    setIsViewingOrEditingJob?.(true);
    if (analystId && job.id) navigate(`/analyst/${analystId}/jobs/${job.id}`, { replace: true });
  };

  const handleBackToList = () => {
    setIsCreatingOrViewingJob(false);
    setSelectedJob(undefined);
    setIsViewingOrEditingJob?.(false);
    if (analystId) {
      navigate(`/analyst/${analystId}/jobs`, { replace: true });
      onRefresh();
    }
  };

  // Update the selected job name when the user changes it
  const handleJobNameChange = (newName: string) => {
    setSelectedJob(prev => (prev ? { ...prev, name: newName } : prev));
  };

  if (isCreatingOrViewingJob) {
    const jobDisplayName = selectedJob?.name?.trim() || "New Workflow";

    return (
      <Flex direction="column" height="100%" bg="white" flex="1" minH={0}>
        {/* Breadcrumb */}
        <HStack px={3} pt={2} pb={0} flexWrap="wrap" spacing={1} align="center" lineHeight="1.25" flexShrink={0}>
          <Button
            variant="link"
            size="sm"
            onClick={() => navigate("/analyst")}
            color="blue.600"
            fontWeight="600"
            fontSize="sm"
            minH="auto"
            p={0}
            h="auto"
            textDecoration="underline"
            textUnderlineOffset="2px"
            _hover={{ color: "blue.700", textDecoration: "underline" }}
          >
            Analysts
          </Button>
          <Text as="span" color="gray.400" fontSize="xs" mb={0} userSelect="none" aria-hidden>
            /
          </Text>
          <Button
            variant="link"
            size="sm"
            onClick={handleBackToList}
            color="blue.600"
            fontWeight="600"
            fontSize="sm"
            minH="auto"
            p={0}
            h="auto"
            textDecoration="underline"
            textUnderlineOffset="2px"
            _hover={{ color: "blue.700", textDecoration: "underline" }}
          >
            {analystName || "Analyst"}
          </Button>
          <Text as="span" color="gray.400" fontSize="xs" mb={0} userSelect="none" aria-hidden>
            /
          </Text>
          <Text as="span" color="gray.600" fontWeight="500" fontSize="sm" mb={0}>
            {jobDisplayName}
          </Text>
        </HStack>

        {/* Job editor */}
        <Box flex="1" minH={0} display="flex" flexDirection="column" overflow="hidden" bg="white">
          <JobDetailView onBack={handleBackToList} selectedJob={selectedJob} onJobNameChange={handleJobNameChange} />
        </Box>
      </Flex>
    );
  }

  // While waiting to open a deep-linked job, show a full-page spinner instead of the list
  if (initialJobId && isLoading) {
    return (
      <Center height="100%">
        <Spinner size="xl" color="purple.600" thickness="3px" />
      </Center>
    );
  }

  return (
    <Box height="100%" bg="white" p={4} display="flex" flexDirection="column">
      {/* Header */}
      <Box maxW="900px" w="100%" mx="auto" mb={3}>
        <HStack spacing={2} align="center" justify="space-between">
          {/* Search bar */}
          <InputGroup size="md" maxW="50%">
            <InputLeftElement pointerEvents="none" height="full">
              <Icon as={AppIcons.search} boxSize={4} color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search workflows..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              bg="gray.50"
              border="1px"
              borderColor="gray.200"
              fontSize="sm"
              _hover={{ borderColor: "gray.300", bg: "white" }}
              _focus={{
                borderColor: "purple.400",
                boxShadow: "0 0 0 1px var(--chakra-colors-purple-400)",
                bg: "white",
              }}
              _placeholder={{ color: "gray.400" }}
            />
          </InputGroup>

          <HStack spacing={2} align="center">
            {/* Filter menu */}
            <Menu closeOnSelect>
              <MenuButton
                as={Button}
                size="sm"
                variant={filterMode !== "all" ? "solid" : "outline"}
                colorScheme={filterMode !== "all" ? "purple" : "gray"}
                bg={filterMode !== "all" ? "purple.700" : "gray.100"}
                leftIcon={<Icon as={AppIcons.filter} boxSize={3} />}
                fontWeight="600"
                flexShrink={0}
                px={2}
                py={1}
              >
                {filterLabel[filterMode]}
              </MenuButton>
              <MenuList minW="160px" fontSize="sm">
                <MenuItem onClick={() => setFilterMode("all")} fontWeight={filterMode === "all" ? "600" : "normal"}>
                  All Workflows ({jobs.length})
                </MenuItem>
                <MenuDivider />
                <MenuItem onClick={() => setFilterMode("live")} fontWeight={filterMode === "live" ? "600" : "normal"}>
                  Live Workflows ({liveCount})
                </MenuItem>
                <MenuItem
                  onClick={() => setFilterMode("paused")}
                  fontWeight={filterMode === "paused" ? "600" : "normal"}
                >
                  Paused Workflows ({jobs.length - liveCount})
                </MenuItem>
              </MenuList>
            </Menu>

            {/* Create New Workflow */}
            <Button
              variant="outline"
              size="sm"
              fontWeight="700"
              colorScheme="purple"
              leftIcon={<Icon as={AppIcons.plus} boxSize={3} />}
              onClick={handleCreateNewJob}
              flexShrink={0}
              px={3}
            >
              New Workflow
            </Button>
          </HStack>
        </HStack>
      </Box>

      {/* Job List */}
      <Box flex="1" minH={0} overflowY="auto">
        <JobList jobs={filteredJobs} isLoadingJobs={isLoading} onJobClick={handleJobClick} onRefresh={onRefresh} />
      </Box>
    </Box>
  );
};

export default AnalystJobs;
